import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Download, AlertTriangle, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { QrisAccountDetail } from '../types';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/helpers';
import { QRIS_TYPE_LABEL, parsePaydiaImportResponse, qrisBlobErrorMessage, qrisErrorMessage, qrisStatusBadge, saveBlob, todayStamp } from '../utils/qris';

interface Props {
  accountId: string | null;
  onClose: () => void;
  onChanged: () => void;
}

type Mode = null | 'uploaded' | 'activate' | 'paydiaClient' | 'reject' | 'refund';

const MID_PATTERN = /^\d{15}$/;
const PAN_PATTERN = /^\d{16,19}$/;
const CLIENT_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;

const ACTION_LABEL: Record<string, string> = {
  submitted: 'Dikirim pemilik',
  exported: 'Diekspor',
  uploaded: 'Ditandai sudah diunggah',
  activated: 'Diaktifkan',
  paydia_client_changed: 'Kredensial Paydia diganti',
  rejected: 'Ditolak',
  refunded: 'Direfund',
};

const humanize = (key: string) => key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

const EXT: Record<string, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'application/pdf': 'pdf' };

const isBankKey = (key: string) => /account|bank|rekening/i.test(key);

const QrisRegistrationDetailSlideOver: React.FC<Props> = ({ accountId, onClose, onChanged }) => {
  const { canManage } = useAuth();
  const [account, setAccount] = useState<QrisAccountDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);
  const [mode, setMode] = useState<Mode>(null);
  const [mid, setMid] = useState('');
  const [merchantName, setMerchantName] = useState('');
  const [merchantPan, setMerchantPan] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [pasted, setPasted] = useState('');
  const [text, setText] = useState('');

  // Secret hanya hidup di state form ini; dibersihkan setiap form ditutup/berhasil.
  const resetActivation = () => {
    setMid('');
    setMerchantName('');
    setMerchantPan('');
    setClientId('');
    setClientSecret('');
    setPasted('');
  };

  const loadDetail = useCallback(async (id: string) => {
    const detail = await api.getQrisAccount(id);
    setAccount(detail);
  }, []);

  useEffect(() => {
    if (!accountId) return;
    setAccount(null);
    setMessage(null);
    setMode(null);
    resetActivation();
    setText('');
    setLoading(true);
    loadDetail(accountId)
      .catch((err) => setMessage({ type: 'error', text: qrisErrorMessage(err, 'Gagal memuat pendaftaran.') }))
      .finally(() => setLoading(false));
  }, [accountId, loadDetail]);

  if (!accountId) return null;

  // Satu request pada satu waktu; ref menutup celah dobel klik sebelum state sempat render.
  const run = async (action: () => Promise<string | void>, fallbackError: string, opts?: { blob?: boolean; reload?: boolean }) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setMessage(null);
    try {
      const success = await action();
      if (opts?.reload !== false) {
        try { await loadDetail(accountId); } catch { /* detail lama tetap tampil */ }
        onChanged();
      }
      setMode(null);
      setText('');
      resetActivation();
      if (success) setMessage({ type: 'ok', text: success });
    } catch (err: any) {
      const msg = opts?.blob ? await qrisBlobErrorMessage(err, fallbackError) : qrisErrorMessage(err, fallbackError);
      setMessage({ type: 'error', text: msg });
      // 422 INVALID_STATUS: muat ulang agar tombol sesuai status terbaru.
      if (err?.response?.status === 422 && opts?.reload !== false) {
        try { await loadDetail(accountId); onChanged(); } catch { /* abaikan */ }
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const downloadDoc = (docId: string, type: string, mime: string) =>
    run(async () => {
      const blob = await api.downloadQrisDocument(accountId, docId);
      saveBlob(blob, `${type}.${EXT[mime] ?? 'bin'}`);
    }, 'Gagal mengunduh dokumen.', { blob: true, reload: false });

  const downloadZip = () =>
    run(async () => {
      const blob = await api.downloadQrisDocumentsZip(accountId);
      saveBlob(blob, `dokumen-qris-${todayStamp()}.zip`);
    }, 'Gagal mengunduh dokumen.', { blob: true, reload: false });

  const exportRow = () => {
    if (!account) return;
    run(async () => {
      const blob = await api.exportQrisAccounts(account.type, [account.id]);
      const slug = account.type === 'individual' ? 'perorangan' : 'badan-usaha';
      saveBlob(blob, `paydia-import-${slug}-${todayStamp()}.xlsx`);
      return 'File diekspor. Hapus file setelah diunggah ke Paydia (memuat kata sandi & PIN).';
    }, 'Gagal mengekspor.', { blob: true });
  };

  const markUploaded = () => run(async () => (await api.markQrisUploaded(accountId)).message, 'Gagal menandai sudah diunggah.');

  const midValid = MID_PATTERN.test(mid);
  const panValid = merchantPan === '' || PAN_PATTERN.test(merchantPan);
  const clientValid = CLIENT_PATTERN.test(clientId) && CLIENT_PATTERN.test(clientSecret);
  const activationValid = midValid && panValid && clientValid;
  const pastedParsed = pasted.trim() ? parsePaydiaImportResponse(pasted) : null;
  const applyPasted = (value: string) => {
    setPasted(value);
    const parsed = parsePaydiaImportResponse(value);
    if (!parsed) return;
    setMid(parsed.mid);
    setMerchantPan(parsed.merchantPan);
    setClientId(parsed.clientId);
    setClientSecret(parsed.clientSecret);
  };
  const activate = () => {
    if (!activationValid) return;
    run(async () => (await api.activateQrisAccount(accountId, {
      mid,
      merchantPan: merchantPan || undefined,
      clientId,
      clientSecret,
      merchantName: merchantName.trim() || undefined,
    })).message, 'Gagal mengaktifkan.');
  };
  // Ganti kredensial akun aktif: MID tidak berubah; MID dari JSON tempelan hanya pengaman salah toko.
  const midMismatch = mode === 'paydiaClient' && mid !== '' && mid !== account?.mid;
  const changeClient = () => {
    if (!clientValid || midMismatch) return;
    run(async () => (await api.changeQrisPaydiaClient(accountId, { mid: mid || undefined, clientId, clientSecret })).message, 'Gagal mengganti kredensial.');
  };
  const reject = () => {
    if (!text.trim()) return;
    run(async () => (await api.rejectQrisAccount(accountId, { reason: text.trim() })).message, 'Gagal menolak.');
  };
  const refund = () => {
    if (!text.trim()) return;
    run(async () => (await api.refundQrisAccount(accountId, { note: text.trim() })).message, 'Gagal menandai refund.');
  };

  const openMode = (m: Mode) => { setMode(m); setText(''); resetActivation(); setMessage(null); };
  const pasteField = (
    <>
      <label className="block text-gray-700">Tempel respons Import Merchant Paydia</label>
      <textarea value={pasted} onChange={(e) => applyPasted(e.target.value)} rows={4} spellCheck={false} autoComplete="off"
        className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-xs"
        placeholder='{"status":"success","data":{"merchant_mid":"...","merchant_pan":"...","secret_id":"...","secret_key":"..."}}' />
      {pasted.trim() !== '' && (
        pastedParsed
          ? <p className="text-xs text-green-700">Terbaca: MID, PAN, client ID & secret terisi.</p>
          : <p className="text-xs text-red-600">Bukan respons Import Merchant Paydia (butuh merchant_mid, secret_id, secret_key).</p>
      )}
    </>
  );
  const clientFields = (
    <>
      <label className="block text-gray-700">Client ID (secret_id)</label>
      <input value={clientId} onChange={(e) => setClientId(e.target.value.trim())} autoComplete="off" spellCheck={false}
        className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono" />
      <label className="block text-gray-700">Client secret (secret_key)</label>
      <input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value.trim())} autoComplete="new-password"
        className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono" />
      {(clientId !== '' || clientSecret !== '') && !clientValid && (
        <p className="text-xs text-red-600">Client ID & secret: 8-128 karakter huruf/angka.</p>
      )}
    </>
  );
  const thirdRejection = account ? account.rejectionCount === account.maxRejections - 1 : false;
  const btn = 'px-3 py-2 rounded-md disabled:opacity-50';

  return (
    <div className="fixed inset-0 overflow-hidden z-50">
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
        <div className="w-screen max-w-xl">
          <div className="h-full flex flex-col bg-white shadow-xl overflow-y-auto">
            <div className="px-6 py-5 bg-gray-50 border-b flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Detail pendaftaran QRIS</h2>
                {account && <p className="mt-1 text-sm text-gray-500">{account.merchantName || account.registrationMerchantName || '-'}</p>}
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>

            {loading || !account ? (
              <div className="p-6">{loading ? <LoadingSpinner /> : message && <p className="text-sm text-red-600">{message.text}</p>}</div>
            ) : (
              <div className="flex-1 px-6 py-5 space-y-6 text-sm">
                {message && (
                  <div className={`p-3 rounded-md ${message.type === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{message.text}</div>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  {qrisStatusBadge(account.status)}
                  <span className="text-gray-500">{QRIS_TYPE_LABEL[account.type]}</span>
                  <span className="text-gray-500">Ditolak {account.rejectionCount}/{account.maxRejections}</span>
                  {account.mid && <span className="font-mono text-gray-700">MID {account.mid}</span>}
                  {account.merchantPan && <span className="font-mono text-gray-700">PAN {account.merchantPan}</span>}
                  {account.status === 'active' && !account.hasPaydiaClient && (
                    <span className="text-red-600">Client ID/secret Paydia belum diisi: QR tidak bisa dibuat</span>
                  )}
                </div>

                {account.rejectionReason && (account.status === 'rejected' || account.status === 'refund_pending') && (
                  <div className="p-3 rounded-md bg-red-50 text-red-800"><div className="text-xs uppercase">Alasan penolakan</div>{account.rejectionReason}</div>
                )}
                {account.refundNote && <div className="p-3 rounded-md bg-gray-50"><div className="text-xs uppercase text-gray-500">Catatan refund</div>{account.refundNote}</div>}

                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Data</h3>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-3 bg-gray-50 rounded-lg p-4">
                    <div><dt className="text-xs uppercase text-gray-500">Company</dt>
                      <dd className="font-medium text-gray-900"><Link to={`/companies/${account.company.id}`} className="text-primary-600 hover:underline">{account.company.brandName || account.company.name}</Link></dd></div>
                    <div><dt className="text-xs uppercase text-gray-500">Email</dt><dd className="text-gray-900 break-all">{account.registrationEmail ?? '-'}</dd></div>
                    {Object.entries(account.data ?? {}).map(([key, value]) => (
                      <div key={key}><dt className="text-xs uppercase text-gray-500">{humanize(key)}</dt><dd className="text-gray-900 break-words">{value === '' || value == null ? '-' : String(value)}</dd></div>
                    ))}
                  </dl>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Kredensial</h3>
                  <p className="text-gray-700 flex items-center gap-1">
                    {account.hasCredentials ? <><Lock className="h-4 w-4" /> Tersimpan terenkripsi</> : 'Sudah dihapus'}
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Dokumen</h3>
                  {account.documents.length === 0 ? <p className="text-gray-500">Tidak ada dokumen.</p> : (
                    <div className="flex flex-wrap gap-2">
                      {account.documents.map((d) => (
                        <button key={d.id} disabled={busy} onClick={() => downloadDoc(d.id, d.type, d.mime)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
                          <Download className="h-3.5 w-3.5" /> {humanize(d.type)}
                        </button>
                      ))}
                      <button disabled={busy} onClick={downloadZip} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
                        <Download className="h-3.5 w-3.5" /> Unduh semua (ZIP)
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Riwayat</h3>
                  {account.events.length === 0 ? <p className="text-gray-500">Belum ada riwayat.</p> : (
                    <ul className="space-y-2">
                      {account.events.map((e, i) => (
                        <li key={i} className="border-l-2 border-gray-200 pl-3">
                          <div className="text-gray-900">{ACTION_LABEL[e.action] ?? e.action}{(e.adminName || e.actorName) && <span className="text-gray-500"> oleh {e.adminName || e.actorName}</span>}</div>
                          {e.note && <div className="text-gray-600">{e.note}</div>}
                          <div className="text-xs text-gray-400">{formatDate(e.createdAt)}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {canManage && account.status === 'submitted' && (
                  <div className="border-t pt-4 space-y-3">
                    {mode === 'uploaded' ? (
                      <div className="p-3 rounded-md bg-yellow-50 border border-yellow-200 text-yellow-900 space-y-2">
                        <div>Kata sandi &amp; PIN akan dihapus permanen. Pastikan file sudah diunggah ke Paydia.</div>
                        <div className="flex gap-2">
                          <button disabled={busy} onClick={markUploaded} className={`${btn} bg-primary-600 text-white hover:bg-primary-700`}>{busy ? 'Memproses...' : 'Ya, tandai sudah diunggah'}</button>
                          <button disabled={busy} onClick={() => setMode(null)} className={`${btn} border border-gray-300 hover:bg-gray-50`}>Batal</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        <button disabled={busy} onClick={exportRow} className={`${btn} border border-gray-300 hover:bg-gray-50`}>Ekspor baris ini</button>
                        <button disabled={busy} onClick={() => openMode('uploaded')} className={`${btn} bg-primary-600 text-white hover:bg-primary-700`}>Tandai Sudah Diunggah</button>
                      </div>
                    )}
                  </div>
                )}

                {canManage && account.status === 'uploaded' && (
                  <div className="border-t pt-4 space-y-3">
                    {mode === null && (
                      <div className="flex flex-wrap gap-2">
                        <button disabled={busy} onClick={() => openMode('activate')} className={`${btn} bg-primary-600 text-white hover:bg-primary-700`}>Aktifkan</button>
                        <button disabled={busy} onClick={() => openMode('reject')} className={`${btn} border border-red-300 text-red-700 hover:bg-red-50`}>Tolak</button>
                      </div>
                    )}
                    {mode === 'activate' && (
                      <div className="space-y-2">
                        {pasteField}
                        <label className="block text-gray-700">MID (15 digit)</label>
                        <input value={mid} inputMode="numeric" maxLength={15} onChange={(e) => setMid(e.target.value.replace(/\D/g, ''))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono" placeholder="000000000000000" />
                        {mid.length > 0 && !midValid && <p className="text-xs text-red-600">MID harus tepat 15 digit angka ({mid.length}/15).</p>}
                        <label className="block text-gray-700">PAN (opsional)</label>
                        <input value={merchantPan} inputMode="numeric" maxLength={19} onChange={(e) => setMerchantPan(e.target.value.replace(/\D/g, ''))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono" />
                        {!panValid && <p className="text-xs text-red-600">PAN harus 16-19 digit angka.</p>}
                        {clientFields}
                        <label className="block text-gray-700">Nama merchant (opsional)</label>
                        <input value={merchantName} onChange={(e) => setMerchantName(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" />
                        <p className="text-xs text-gray-500">QRIS ditautkan ke semua cabang company ini.</p>
                        <div className="flex gap-2">
                          <button disabled={busy || !activationValid} onClick={activate} className={`${btn} bg-primary-600 text-white hover:bg-primary-700`}>{busy ? 'Memproses...' : 'Aktifkan'}</button>
                          <button disabled={busy} onClick={() => setMode(null)} className={`${btn} border border-gray-300 hover:bg-gray-50`}>Batal</button>
                        </div>
                      </div>
                    )}
                    {mode === 'reject' && (
                      <div className="space-y-2">
                        {thirdRejection && (
                          <div className="flex gap-2 p-3 rounded-md bg-red-50 text-red-800">
                            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            <div>Penolakan ke-3 → antrean refund Rp35.000. Pendaftaran tidak bisa dikirim ulang.</div>
                          </div>
                        )}
                        <label className="block text-gray-700">Alasan penolakan (dilihat pemilik)</label>
                        <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} rows={3} className="w-full border border-gray-300 rounded-md px-3 py-2" />
                        <div className="flex gap-2">
                          <button disabled={busy || !text.trim()} onClick={reject} className={`${btn} bg-red-600 text-white hover:bg-red-700`}>{busy ? 'Memproses...' : thirdRejection ? 'Tolak & masuk antrean refund' : 'Tolak'}</button>
                          <button disabled={busy} onClick={() => setMode(null)} className={`${btn} border border-gray-300 hover:bg-gray-50`}>Batal</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {canManage && account.status === 'active' && (
                  <div className="border-t pt-4 space-y-3">
                    {mode === null && (
                      <div className="flex flex-wrap gap-2">
                        <button disabled={busy} onClick={() => openMode('paydiaClient')} className={`${btn} border border-gray-300 hover:bg-gray-50`}>Ganti kredensial Paydia</button>
                      </div>
                    )}
                    {mode === 'paydiaClient' && (
                      <div className="space-y-2">
                        <p className="text-xs text-gray-500">MID tetap {account.mid}. Token lama dibuang; QR berikutnya memakai kredensial baru.</p>
                        {pasteField}
                        {midMismatch && <p className="text-xs text-red-600">MID di respons ({mid}) berbeda dengan akun ini. Pastikan respons milik toko yang sama.</p>}
                        {clientFields}
                        <div className="flex gap-2">
                          <button disabled={busy || !clientValid || midMismatch} onClick={changeClient} className={`${btn} bg-primary-600 text-white hover:bg-primary-700`}>{busy ? 'Memproses...' : 'Simpan kredensial'}</button>
                          <button disabled={busy} onClick={() => setMode(null)} className={`${btn} border border-gray-300 hover:bg-gray-50`}>Batal</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {canManage && account.status === 'refund_pending' && (
                  <div className="border-t pt-4 space-y-3">
                    {Object.entries(account.data ?? {}).filter(([k]) => isBankKey(k)).length > 0 && (
                      <div className="p-3 rounded-md bg-gray-50">
                        <div className="text-xs uppercase text-gray-500 mb-1">Rekening tujuan refund</div>
                        {Object.entries(account.data).filter(([k]) => isBankKey(k)).map(([k, v]) => <div key={k}>{humanize(k)}: {String(v)}</div>)}
                      </div>
                    )}
                    {mode === 'refund' ? (
                      <div className="space-y-2">
                        <label className="block text-gray-700">Catatan / bukti refund</label>
                        <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} rows={3} className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Transfer BCA 31 Okt, ref 12345" />
                        <div className="flex gap-2">
                          <button disabled={busy || !text.trim()} onClick={refund} className={`${btn} bg-primary-600 text-white hover:bg-primary-700`}>{busy ? 'Memproses...' : 'Simpan'}</button>
                          <button disabled={busy} onClick={() => setMode(null)} className={`${btn} border border-gray-300 hover:bg-gray-50`}>Batal</button>
                        </div>
                      </div>
                    ) : (
                      <button disabled={busy} onClick={() => openMode('refund')} className={`${btn} bg-primary-600 text-white hover:bg-primary-700`}>Tandai Sudah Direfund</button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QrisRegistrationDetailSlideOver;
